import { useState, useEffect } from "react";
import { Text, Loader, Progress } from "@mantine/core";
import { Button, Alert, Badge, DataTable } from "@xplatform/design-system";
import { IconPlayerPlay, IconEye } from "@tabler/icons-react";
import { useEvaluation } from "@hooks/useEvaluation";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type { EvaluationSummaryViewModel } from "@models/evaluation";
import { EvaluationDetailDialog } from "./EvaluationDetailDialog";
import { formatAccuracy, formatDuration, getAccuracyColor } from "@utils/evaluationFormatters";
import "./EvaluationResults.scss";

interface EvaluationResultsProps {
    doctypeId: string;
    flowBaseId: string;
    flowVersion: number;
}

export const EvaluationResults = ({
    doctypeId,
    flowBaseId,
    flowVersion,
}: EvaluationResultsProps) => {
    const { canViewEvaluation, canTriggerEvaluation } = useAdminPermissions();
    const {
        evaluationList,
        evaluationDetails,
        isLoadingList,
        isLoadingDetail,
        isTriggeringEvaluation,
        evaluationInProgress,
        evaluationStatus,
        error,
        triggerEvaluation,
        loadEvaluations,
        clearError,
        loadEvaluationDetail,
    } = useEvaluation({ doctypeId, flowBaseId, flowVersion });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProcessId, setSelectedProcessId] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (canViewEvaluation) {
            loadEvaluations();
        }
    }, [canViewEvaluation, loadEvaluations]);

    const handleViewDetails = async (processId: string) => {
        setSelectedProcessId(processId);
        setDialogOpen(true);
        await loadEvaluationDetail(processId);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedProcessId(null);
    };

    const handleTrigger = async () => {
        clearError();
        await triggerEvaluation();
    };

    if (!canViewEvaluation) {
        return null;
    }

    return (
        <div className="evaluation-results">
            <div className="evaluation-results__header">
                <div className="evaluation-results__header-content">
                    <Text size="lg" fw={600}>
                        Evaluation Results
                    </Text>
                </div>
                <div className="evaluation-results__header-actions">
                    {canTriggerEvaluation && (
                        <Button
                            size="sm"
                            leftSection={<IconPlayerPlay size={16} />}
                            onClick={handleTrigger}
                            disabled={isTriggeringEvaluation || evaluationInProgress}
                        >
                            {isTriggeringEvaluation ? "Starting..." : "Run Evaluation"}
                        </Button>
                    )}
                </div>
            </div>

            {evaluationInProgress && (
                <Alert color="blue" className="evaluation-results__status">
                    <div className="evaluation-results__status-content">
                        <Text size="sm">{evaluationStatus}</Text>
                        <Progress
                            value={100}
                            animated
                            className="evaluation-results__progress"
                        />
                    </div>
                </Alert>
            )}

            {error && (
                <Alert
                    color="red"
                    withCloseButton
                    onClose={clearError}
                    className="evaluation-results__error"
                >
                    {error}
                </Alert>
            )}

            {isLoadingList && !evaluationList ? (
                <div className="evaluation-results__loading">
                    <Loader />
                    <Text size="sm" c="dimmed">
                        Loading evaluations...
                    </Text>
                </div>
            ) : evaluationList && evaluationList.items.length > 0 ? (
                <div className="evaluation-results__table-container">
                    <DataTable<EvaluationSummaryViewModel>
                        columns={[
                            {
                                key: "processId",
                                header: "Process ID",
                                render: (item) => (
                                    <span className="evaluation-results__process-id">
                                        {item.processId}
                                    </span>
                                ),
                            },
                            {
                                key: "fileCount",
                                header: "File Count",
                                render: (item) => item.fileCount,
                            },
                            {
                                key: "accuracy",
                                header: "Accuracy",
                                render: (item) => (
                                    <Badge color={getAccuracyColor(item.avgAccuracy)} size="sm">
                                        {formatAccuracy(item.avgAccuracy)}
                                    </Badge>
                                ),
                            },
                            {
                                key: "duration",
                                header: "Duration",
                                render: (item) => (
                                    <Text size="sm" c="dimmed">
                                        {formatDuration(item.avgDuration)}
                                    </Text>
                                ),
                            },
                            {
                                key: "date",
                                header: "Date",
                                render: (item) => (
                                    <Text size="sm" c="dimmed">
                                        {item.createdAt.toLocaleString()}
                                    </Text>
                                ),
                            },
                            {
                                key: "actions",
                                header: "Actions",
                                align: "right",
                                render: (item) => (
                                    <div className="evaluation-results__actions-cell">
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            leftSection={<IconEye size={14} />}
                                            onClick={() => handleViewDetails(item.processId)}
                                        >
                                            View Details
                                        </Button>
                                    </div>
                                ),
                            },
                        ]}
                        data={[...new Map(evaluationList.items.map((item: EvaluationSummaryViewModel) => [item.processId, item])).values()]}
                        getRowKey={(item) => item.processId}
                    />
                </div>
            ) : (
                <div className="evaluation-results__empty">
                    <Text size="md" c="dimmed">
                        No evaluation results yet
                    </Text>
                    <Text size="sm" c="dimmed">
                        Run an evaluation to see results here
                    </Text>
                </div>
            )}

            <EvaluationDetailDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                processId={selectedProcessId || ""}
                evaluationDetails={evaluationDetails}
                isLoading={isLoadingDetail}
            />
        </div>
    );
};